import type { GatewayFrame } from "../../lib/use-gateway-frame";
import styles from "./GatewayIframe.module.css";

interface Props {
  frame: GatewayFrame;
  /** The frame's accessible name. */
  title: string;
  /** Shown at its reported height; otherwise kept at no height, still loading. */
  visible: boolean;
  /** Height before the page has reported one. */
  initialHeight?: number;
}

/**
 * A framed gateway page. The gateway page reports its own height, so the frame is sized to its
 * content and never scrolls inside. It stays in the page, at no height, until the caller says it
 * is worth showing: a frame can't load while it's out of the layout, and its width has to be
 * right for the height it reports to be.
 *
 * No `sandbox`: the pages' server links use <base target="_top"> to open Marketplace pages, which
 * a sandbox without top-navigation would block. The gateway is our own service, and it is the
 * gateway that limits who may frame it.
 */
export default function GatewayIframe({ frame, title, visible, initialHeight = 240 }: Props) {
  if (!frame.src) return null;
  return (
    <iframe
      key={frame.attempt}
      ref={frame.iframeRef}
      src={frame.src}
      title={title}
      scrolling="no"
      referrerPolicy="strict-origin-when-cross-origin"
      className={visible ? styles.frame : `${styles.frame} ${styles.pending}`}
      // eslint-disable-next-line react/forbid-dom-props -- the height is whatever the framed page reports
      style={visible ? { height: frame.height ?? initialHeight } : undefined}
    />
  );
}
